/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useGeneralData } from "./hooks";
import { authSetupvalues } from "./helpers";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import { RadixSelectControl } from "../../../ui/radix-select";
import { Button } from "../../../ui/button";
import { ShadcnHeading } from "../../../ui/shadcn-heading";

const General: React.FC = () => {
  const { generalAuth, setGeneralAuth, submitHandler, cancelHandler } = useGeneralData();
  const selectOptions: string[] = ["None", "RADIUS", "TACACS+", "SAML"];

  const handleRadiobuttonGroup = (value: string) => {
    if (value === authSetupvalues.spiderNetAuth) {
      setGeneralAuth({ ...generalAuth, authenticationSetup: value, authenticationExternal: "" });
    } else {
      setGeneralAuth({ ...generalAuth, authenticationSetup: value });
    }
  };

  const handleChangeOnSelect = (value: string) => {
    setGeneralAuth({ ...generalAuth, authenticationExternal: value === "None" ? "" : value });
  };

  let isEnableSave: boolean = false;
  if (generalAuth.authenticationSetup === authSetupvalues.spiderNetAuth && !!!generalAuth.authenticationExternal) {
    isEnableSave = true;
  } else if (generalAuth.authenticationSetup !== authSetupvalues.spiderNetAuth && !!generalAuth.authenticationExternal) {
    isEnableSave = true;
  } else {
    isEnableSave = false;
  }

  const radioOptions = [
    {
      value: authSetupvalues.spiderNetAuth,
      label: "AMS Only, Users will be authenticated against AMS's internal database.",
    },
    {
      value: authSetupvalues.externalAuth,
      label: "External Only, Users will be authenticated against an external AAA server.",
    },
    {
      value: authSetupvalues.combinedAuth,
      label: "Combined. If external authentication fails, Users will be authenticated against AMS's internal database.",
    },
  ];

  return (
    <div
      className="flex flex-col w-full overflow-y-auto px-1 md:px-1"
      style={{
        color: "var(--foreground)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius)",
        padding: "24px",
        paddingBottom: "10px",
        boxShadow: "var(--shadow-card)",
        backgroundColor: "var(--background)",
      }}
    >
      {/* Heading */}

      <h2 style={{ margin: 0, padding: 0, lineHeight: 0.5, marginBottom: "10px" }}>
        <ShadcnHeading className="text-sm font-semibold text-foreground" as="span">
          General Settings
        </ShadcnHeading>
      </h2>

      <div className="mt-3 "
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius)",
          padding: "10px 10px 24px 10px",
         
          boxShadow: "var(--shadow-card)",
          backgroundColor: "var(--background)",
        }}
      >
        <form autoComplete="off">
          <div className="space-y-5">
            {/* Radio Group */}
            <div>
              <RadioGroup
                value={generalAuth?.authenticationSetup}
                onValueChange={handleRadiobuttonGroup}
                className="flex flex-col gap-3"
              >
                {radioOptions.map((option) => {
                  const title = option.label.split(",")[0];
                  const description = option.label.split(",")[1];
                  return (
                    <Label
                      key={option.value}
                      htmlFor={option.value}
                      className="flex cursor-pointer items-start gap-3 rounded-md p-2 transition-colors hover:bg-accent"
                    >
                      <RadioGroupItem value={option.value} id={option.value} className="mt-0.5" />
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium text-foreground m-0">{title}</p>
                        <p className="text-sm text-muted-foreground normal-case m-0">{description}</p>
                      </div>
                    </Label>
                  );
                })}
              </RadioGroup>
            </div>


          </div>
        </form>
      </div>
      {/* External Authentication Type Select */}

      <div className="space-y-2 max-w-[200px] mt-5">
        <Label htmlFor="authenticationExternal" className="text-sm font-medium mb-4 block">
          External Authentication Type
        </Label>
        <RadixSelectControl
          value={
            selectOptions.includes(generalAuth?.authenticationExternal)
              ? generalAuth?.authenticationExternal
              : "None"
          }
          options={selectOptions.map(opt => ({ value: opt, label: opt }))}
          onValueChange={handleChangeOnSelect}
          disabled={generalAuth.authenticationSetup === authSetupvalues.spiderNetAuth}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-2">
        <Button type="button" variant="outline" onClick={cancelHandler}>
          Cancel
        </Button>
        <Button type="button" disabled={!isEnableSave} onClick={submitHandler}>
          Save
        </Button>
      </div>
    </div >
  );
};

export default General;
